import { NEW_VIEW } from '@/constants/dashboardConstants';
import useProject from '@API/project/useProject';
import {
  generateAutoGeneratedCurvePanel,
  generateAutoGeneratedDataframePanel,
  generateAutoGeneratedImagePanel,
  generateAutoGeneratedMetricsPanel
} from '@experiment-management-shared';
import LayoutWorker from '@experiment-management-shared/components/Charts/workers/LayoutWorker';
import {
  CURVES_SECTION_NAME,
  MEDIA_SECTION_NAME,
  TABLES_SECTION_NAME
} from '@experiment-management-shared/constants';
import { DEFAULT_EXPERIMENTS_PER_PAGE } from '@experiment-management-shared/constants/experimentConstants';
import { Experiment, PanelSection } from '@experiment-management-shared/types';
import { DEFAULT_QUERY } from '@experiment-management-shared/utils/view';
import api, { QueryConfig } from '@shared/api';
import useDeepMemo from '@shared/hooks/useDeepMemo';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useExperimentsCurveNames } from './useExperimentsCurveNames';
import { useExperimentsDataframeNames } from './useExperimentsDataframeNames';
import { useExperimentsImageNames } from './useExperimentsImageNames';
import { useExperimentsMetricNames } from './useExperimentsMetricNames';

// @todo: improve this logic if possible reusing the `DEFAULT_SORTING` const
const DEFAULT_SORTING_REQUEST = [
  {
    order: 'desc',
    keyName: 'runActive',
    source: 'computed_metadata'
  },
  {
    order: 'desc',
    keyName: 'end_server_timestamp',
    source: 'end_time'
  }
];

function useDefaultExperimentKeys() {
  const { data: project } = useProject();
  const projectId = project?.projectId;

  return useQuery(
    ['default-experiments', { projectId }],
    async ({ signal }) => {
      const {
        data: { experiments }
      } = await api.post<{ experiments: Experiment[] }>(
        'query/experiments/search',
        {
          deleted: false,
          ignorePinnedExperimentsFirst: true,
          multipleSort: DEFAULT_SORTING_REQUEST,
          limit: DEFAULT_EXPERIMENTS_PER_PAGE,
          rulesTree: DEFAULT_QUERY.rulesTree,
          projectId,
          page: 1,
          viewId: NEW_VIEW
        },
        { signal }
      );

      return experiments.map(experiment => experiment.experimentKey);
    },
    { enabled: !!projectId }
  );
}

export const useApplyAutoGeneratedSections = ({
  config,
  enabled = true,
  sections,
  viewExperimentKeys
}: {
  config: QueryConfig<string[]>;
  enabled?: boolean;
  sections?: PanelSection[];
  viewExperimentKeys: string[] | null;
}) => {
  const {
    data: defaultExperimentKeys,
    isSuccess: areDefaultExperimentKeysLoaded
  } = useDefaultExperimentKeys();
  const experimentKeys = useDeepMemo(
    () => viewExperimentKeys ?? defaultExperimentKeys ?? [],
    [defaultExperimentKeys, viewExperimentKeys]
  );
  const parameters = { experimentKeys };

  const [
    sectionsWithAutoGeneratedPanels,
    setSectionsWithAutoGeneratedPanels
  ] = useState<PanelSection[] | null>(null);

  const { data: metricNames } = useExperimentsMetricNames(parameters, config);
  const { data: curveNames } = useExperimentsCurveNames(parameters, config);
  const { data: dataframeNames } = useExperimentsDataframeNames(
    parameters,
    config
  );
  const { data: imageNames } = useExperimentsImageNames(parameters, config);

  const experimentCount = experimentKeys.length;
  const isLoadingNames =
    !metricNames || !curveNames || !dataframeNames || !imageNames;

  useEffect(() => {
    if (!areDefaultExperimentKeysLoaded || isLoadingNames || !enabled) return;

    let cancelled = false;

    addPanelsToSections({
      curveNames,
      dataframeNames,
      experimentCount,
      imageNames,
      metricNames,
      sections
    }).then(newSections => {
      if (!cancelled) {
        setSectionsWithAutoGeneratedPanels(newSections);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    areDefaultExperimentKeysLoaded,
    curveNames,
    dataframeNames,
    enabled,
    experimentCount,
    imageNames,
    isLoadingNames,
    metricNames,
    sections
  ]);

  return {
    isLoading:
      !areDefaultExperimentKeysLoaded ||
      isLoadingNames ||
      sectionsWithAutoGeneratedPanels === null,
    sections: sectionsWithAutoGeneratedPanels ?? sections
  };
};

async function addPanelsToSections({
  curveNames,
  dataframeNames,
  experimentCount,
  imageNames,
  metricNames,
  sections = []
}: {
  curveNames: string[];
  dataframeNames: string[];
  experimentCount: number;
  imageNames: string[];
  metricNames: string[];
  sections?: PanelSection[];
}) {
  let newSections = sections;

  if (metricNames.length) {
    const metricPanels = metricNames.map(generateAutoGeneratedMetricsPanel);

    newSections = await LayoutWorker.mergeMetricsWithAutogeneratedPanels({
      autoGeneratedMetricsPanels: metricPanels,
      initialSections: newSections
    });
  }

  if (imageNames.length) {
    const imagePanel = generateAutoGeneratedImagePanel(
      imageNames,
      experimentCount
    );

    newSections = await LayoutWorker.mergeAssetPanelsWithAutogeneratedPanels({
      autoGeneratedPanels: [imagePanel],
      initialSections: newSections,
      destinationSectionName: MEDIA_SECTION_NAME
    });
  }

  if (dataframeNames.length) {
    const dataframePanels = dataframeNames.map(name =>
      generateAutoGeneratedDataframePanel(name, experimentCount)
    );

    newSections = await LayoutWorker.mergeAssetPanelsWithAutogeneratedPanels({
      autoGeneratedPanels: dataframePanels,
      initialSections: newSections,
      destinationSectionName: TABLES_SECTION_NAME
    });
  }

  if (curveNames.length) {
    const curvePanels = curveNames.map(name =>
      generateAutoGeneratedCurvePanel(name, experimentCount)
    );

    newSections = await LayoutWorker.mergeAssetPanelsWithAutogeneratedPanels({
      autoGeneratedPanels: curvePanels,
      initialSections: newSections,
      destinationSectionName: CURVES_SECTION_NAME
    });
  }

  return newSections;
}
